import React, { useState, useEffect } from 'react';

type Point = {
  x: number;
  y: number;
  label?: string;
  number?: number;
};

type Line = {
  start: Point;
  end: Point;
  startArrow?: boolean;
  endArrow?: boolean;
};

type GridConfig = {
  width: number;
  height: number;
  cellSize: number;
  pointRadius: number;
  arrowSize: number;
  lines: Line[];
  points: Point[];
  color: string;
};

type ConfigurableGridProps = {
  config: GridConfig;
  onPointSelect?: (status: number | null) => void;
  defaultSelectedPoint: number | null;
};

type MetrolineProps = {
  values?: number[];
  onPointSelect?: (status: number | null) => void;
  defaultSelectedPoint: number | null;
  color?: string;
};

const calculateArrowAngle = (start: Point, end: Point): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.atan2(dy, dx) * (180 / Math.PI);
};

const adjustEndPoint = (start: Point, end: Point, cellSize: number, pointRadius: number): { x: number, y: number } => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  if (distance === 0) return { x: end.x * cellSize, y: end.y * cellSize };
  
  const ratio = (distance * cellSize - pointRadius) / (distance * cellSize);
  return {
    x: start.x * cellSize + dx * cellSize * ratio,
    y: start.y * cellSize + dy * cellSize * ratio
  };
};

const Arrow: React.FC<{ start: Point; end: Point; cellSize: number; pointRadius: number; arrowSize: number; color: string }> = ({ start, end, cellSize, pointRadius, arrowSize, color }) => {
  const angle = calculateArrowAngle(start, end);

  const adjustedEnd = adjustEndPoint(start, end, cellSize, pointRadius);
  const tipX = adjustedEnd.x;
  const tipY = adjustedEnd.y;

  return (
    <path
      d={`M ${tipX} ${tipY} l ${-arrowSize} ${-arrowSize/2} l 0 ${arrowSize} Z`}
      transform={`rotate(${angle} ${tipX} ${tipY})`}
      fill={color}
    />
  );
};

const ConfigurableGrid: React.FC<ConfigurableGridProps> = ({ config, onPointSelect, defaultSelectedPoint }) => {
  const { width, height, cellSize, pointRadius, arrowSize, lines, points, color } = config;
  const totalWidth = width * cellSize;
  const totalHeight = height * cellSize;
  
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<number | null>(defaultSelectedPoint);

  useEffect(() => {
    if (defaultSelectedPoint !== undefined) {
      setSelectedPoint(defaultSelectedPoint);
      onPointSelect?.(defaultSelectedPoint);
    }
  }, [defaultSelectedPoint, onPointSelect]);

  const handlePointClick = (index: number) => {
    const newSelectedPoint = selectedPoint === index ? null : index;
    setSelectedPoint(newSelectedPoint);
    onPointSelect?.(newSelectedPoint);
  };

  return (
    <div className="p-4">
      <svg 
        width={totalWidth + cellSize} 
        height={210}
      >
        <g transform="translate(20, 20)">
        {lines.map((line, index) => (
          <g key={`line-${index}`}>
            <line
              x1={line.start.x * cellSize}
              y1={line.start.y * cellSize}
              x2={line.end.x * cellSize}
              y2={line.end.y * cellSize}
              stroke={color}
              strokeWidth={5}
            />
            {line.startArrow && (
              <Arrow start={line.end} end={line.start} cellSize={cellSize} pointRadius={pointRadius} arrowSize={arrowSize} color={color} />
            )}
            {line.endArrow && (
              <Arrow start={line.start} end={line.end} cellSize={cellSize} pointRadius={pointRadius} arrowSize={arrowSize} color={color} />
            )}
          </g>
        ))}

        {points.map((point, index) => (
          <g 
            key={`point-${index}`}
            onClick={() => handlePointClick(index)}
            style={{ cursor: 'pointer' }}
          >
            <circle
              cx={point.x * cellSize}
              cy={point.y * cellSize} 
              r={pointRadius}
              fill={selectedPoint === index ? "#EF5350" : "#606060"}
              stroke={selectedPoint === index ? "#EF5350" : color}
              strokeWidth={hoveredPoint === index ? "6" : "4"}
              onMouseEnter={() => setHoveredPoint(index)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
            {point.number !== undefined && point.number > 0 && (
              <text
                x={point.x * cellSize}
                y={point.y * cellSize + 2}
                className="text-sm font-bold text-center"
                textAnchor="middle"
                alignmentBaseline="middle"
                fill="white"
                style={{ fontWeight: 'bold' }}
              >
                {point.number}
              </text>
            )}
            {point.label && (
              <text
                x={point.x * cellSize}
                y={point.y * cellSize + pointRadius * 3.5}
                className="text-base font-medium text-center"
                textAnchor="middle"
                fill="black"
              >
                {point.label}
              </text>
            )}
          </g>
        ))}
        </g>
      </svg>
    </div>
  );
};

const DEFAULT_VALUES = [0, 0, 0, 0, 0];

const Metroline: React.FC<MetrolineProps> = ({ 
  values = DEFAULT_VALUES, 
  onPointSelect, 
  defaultSelectedPoint = null
}) => {
  const config: GridConfig = {
    width: 9,
    height: 2,
    cellSize: 110,
    pointRadius: 17,
    arrowSize: 22,
    color: "#20A942",
    lines: [
      { start: { x: 1, y: 1 }, end: { x: 2, y: 1 }},
      { start: { x: 2, y: 1 }, end: { x: 3, y: 1 }, endArrow: true },
      { start: { x: 3, y: 1 }, end: { x: 4, y: 1 }, startArrow : true},
      { start: { x: 4, y: 1 }, end: { x: 5, y: 1 }, endArrow: true },
      { start: { x: 5, y: 1 }, end: { x: 6, y: 1 }},
      { start: { x: 6, y: 1 }, end: { x: 7, y: 1 }, endArrow: true },
      { start: { x: 7, y: 1 }, end: { x: 8, y: 1 }},
      { start: { x: 8, y: 1 }, end: { x: 9, y: 1 }, endArrow: true },
      { start: { x: 7, y: 1 }, end: { x: 7, y: 0 }},
      { start: { x: 7, y: 0 }, end: { x: 3, y: 0 }},
      { start: { x: 3, y: 1 }, end: { x: 3, y: 0 }, startArrow: true},
    ],
    points: [
      { x: 1, y: 1, label: "Opening", number: values[0] },
      { x: 3, y: 1, label: "Consortium editing", number: values[1] },
      { x: 5, y: 1, label: "Verification", number: values[2] },
      { x: 7, y: 1, label: "Approval", number: values[3] },
      { x: 9, y: 1, label: "End", number: values[4] },
    ]
  };

  return <ConfigurableGrid config={config} onPointSelect={onPointSelect} defaultSelectedPoint={defaultSelectedPoint} />;
};

export default Metroline;